import * as React from 'react';
import * as _ from 'lodash';
import * as qs from 'query-string';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, ExpertSystemActionCreators } from '@nkr/store/index';
import { IExpertSystemOrderResponse, IExpertSystemOrdersByRecipientResponse, IExpertSystemOrderSummaryResponse } from '@nkr/store/expert-system';
import { IBaseResponse, IExpertSystemOrderByRecipient } from '@nkr/models/index';
import { Badge, FormControl, Nav, Well } from 'react-bootstrap';
import { NavItemWrapper, NavWrapper } from '../center-admin/styles';
import { toast } from 'react-toastify';
import { IExpertSystemOrderSummaryWithComments, Section } from './models';
import RecipientDonorsModal from './recipient-donors-modal';
import { downloadFile } from '@nkr/services/download-file';
import Grid from './grid';
import WithdrawDonor from './withdraw-donor';
import { FormEvent } from 'react';

interface IProps {
    centerId: number,
    statusId: number
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getExpertSystemOrders: (centerId: number) => Promise<IExpertSystemOrderSummaryResponse>,
    updateComments: (orderId: number, comments: string) => Promise<IExpertSystemOrderResponse>,
    getExpertSystemOrdersByRecipient: (recipientId: number) => Promise<IExpertSystemOrdersByRecipientResponse>,
    updateExpertSystemOrder: (orderId: number, statusId: number) => Promise<IExpertSystemOrderResponse>,
    doStandardVoucher: (donorId: number, recipientId: number) => Promise<IBaseResponse>,
    doCompatiblePair: (donorId: number, recipientId: number, direct: boolean, surgeryDate: string) => Promise<IBaseResponse>,
    printRequisition: (orderId: number, entityTypeId: number) => Promise<any>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    expertOrders: IExpertSystemOrderSummaryWithComments[],
    withdrawDonor: {
        show: boolean,
        orderId: number,
        alias: string,
        fullName: string
    },
    recipientDonors: {
        show: boolean,
        data: IExpertSystemOrderSummaryWithComments,
        list: IExpertSystemOrderByRecipient[]
    },
    statusFilter: number,
    searchFilter: string
}

class ExpertSystem extends React.Component<TProps, IState> {
    readonly state: IState = {
        expertOrders: [],
        withdrawDonor: {
            show: false,
            orderId: 0,
            alias: '',
            fullName: ''
        },
        recipientDonors: {
            show: false,
            data: null,
            list: []
        },
        statusFilter: 0,
        searchFilter: ''
    };

    componentDidMount = () => {
        this.getData();
    };

    getData = () => {
        let { centerId, statusId } = this.props;

        let parsed = qs.parse(location.search.length > 0 ? location.search : encodeURI(''), { arrayFormat: 'bracket' });

        var searchFilter = parsed.search != null ? parsed.search : this.state.searchFilter;
        var statusFilter = parsed.status != null ? parsed.status : this.state.statusFilter;

        this.props.showLoading('Getting Expert System Orders, one moment please...').then(() => {
            this.props.getExpertSystemOrders(centerId)
                .then(response => {
                    if (response.data.success) {
                        let expertOrders = response.data.results.map(item => ({ ...item, newComments: item.comments, isChanged: false }));

                        this.setState({
                            expertOrders,
                            searchFilter,
                            statusFilter
                        });
                    }
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    };

    getCounts = () => {
        return {
            pendingDonor: this.state.expertOrders.filter(x => x.statusId == Section.PendingDonor).length,
            pendingCenter: this.state.expertOrders.filter(x => x.statusId == Section.PendingCenter).length,
            pendingRecipient: this.state.expertOrders.filter(x => x.statusId == Section.PendingRecipient).length,
            pendingAnalysis: this.state.expertOrders.filter(x => x.statusId == Section.PendingAnalysis).length,
            pendingReview: this.state.expertOrders.filter(x => x.statusId == Section.PendingReview).length,
            pendingDecision: this.state.expertOrders.filter(x => x.statusId == Section.PendingDecision).length,
            completed: this.state.expertOrders.filter(x => x.statusId == Section.Completed).length,
            search: this.state.expertOrders.length
        }
    }

    setUrlParameters = () => {
        let { statusFilter, searchFilter } = this.state;

        let paramList = [];

        if (statusFilter > 0) {
            paramList.push(encodeURI('status=' + statusFilter));
        }

        if (searchFilter != '') {
            paramList.push('search=' + searchFilter);
        }

        let params = paramList.join('&');

        let newUrl = '/center/' + this.props.centerId + '/action-center/expert-system/' + this.props.statusId + '?' + params

        window.history.pushState({}, '', newUrl);
    }

    getFilteredSortedList = () => {
        let filteredList = this.state.expertOrders.filter(x => {
            let statusId = this.props.statusId == Section.Search ? this.state.statusFilter : this.props.statusId;

            let statusFilter = statusId > 0 ? x.statusId == statusId : true;

            let search = this.state.searchFilter.toUpperCase();
            let donorAlias = x.donorAlias.toUpperCase();
            let donorName = x.donorName.toUpperCase();
            let recipientAlias = x.recipientAlias.toUpperCase();
            let recipientName = x.recipientName.toLowerCase();

            let textFilter = search.length >= 3 ? donorAlias.indexOf(search) >= 0 || donorName.indexOf(search) >= 0 || recipientAlias.indexOf(search) >= 0 || recipientName.indexOf(search) >= 0 : true;

            return statusFilter && textFilter;
        });

        return filteredList;
    }

    handleUpdateComments = (orderId: number, comments: string) => {
        let expertOrders = _.cloneDeep(this.state.expertOrders).map(item => {
            if (item.expertSystemId == orderId) {
                item.newComments = comments;
                item.isChanged = item.comments != comments;
            }

            return item;
        })

        this.setState({
            expertOrders
        });
    }

    handleSaveComments = (orderId: number) => {
        let order = this.state.expertOrders.find(x => x.expertSystemId == orderId);

        this.props.showLoading('Updating comments, one moment please...').then(() => {
            this.props.updateComments(orderId, order.newComments)
                .then(response => {
                    if (response.data.success) {
                        toast.success('Comments updated successfully');

                        let expertOrders = _.cloneDeep(this.state.expertOrders).map(item => {
                            if (item.expertSystemId == orderId) {
                                item.comments = item.newComments;
                                item.isChanged = false;
                            }

                            return item;
                        });

                        this.setState({
                            expertOrders
                        });
                    }
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleDiscardCommentChanges = (orderId: number) => {
        let expertOrders = _.cloneDeep(this.state.expertOrders).map(item => {
            if (item.expertSystemId == orderId) {
                item.newComments = item.comments;
                item.isChanged = false;
            }

            return item;
        });

        this.setState({
            expertOrders
        });
    }

    handleOpenOrder = (orderId: number) => {
        let returnTo = encodeURIComponent(window.location.pathname + window.location.search);

        window.open('/center/' + this.props.centerId + '/action-center/expert-system/view-order/' + orderId + '?returnTo=' + returnTo, '_self');
    };

    handleTabChange = (statusId: any) => {
        window.location.href = '/center/' + this.props.centerId + '/action-center/expert-system/' + statusId;
    }

    handleShowDonors = (expertSystemId: number) => {
        let order = this.state.expertOrders.find(x => x.expertSystemId == expertSystemId);

        this.props.showLoading('Retrieving donors, one moment please...').then(() => {
            this.props.getExpertSystemOrdersByRecipient(order.recipientId).then(response => {
                if (response.data.success) {
                    this.setState({
                        recipientDonors: {
                            show: true,
                            data: order,
                            list: response.data.results
                        }
                    }, () => {
                        this.props.hideLoading();
                    });
                }
            });
        });
    }

    handleDonorCloseModal = () => {
        this.setState({
            recipientDonors: {
                show: false,
                data: null,
                list: []
            }
        });
    }

    handleUpdateExpertSystemOrder = async (orderId: number, statusId: number, hideLoading: boolean) => {
        await this.props.showLoading('Updating expert system entry, one moment please...');

        let updateResponse = await this.props.updateExpertSystemOrder(orderId, statusId);

        if (hideLoading) {
            this.props.hideLoading();
        }

        return updateResponse;
    }

    handleDoStandardVoucher = async (donorId: number, recipientId: number) => {
        await this.props.showLoading('Converting to standard voucher, one moment please...');

        let svResponse = await this.props.doStandardVoucher(donorId, recipientId);

        this.props.hideLoading();

        return svResponse;
    }

    handleDoCompatiblePair = async (donorId: number, recipientId: number, direct: boolean, surgeryDate: string) => {
        await this.props.showLoading('Converting to compatible pair, one moment please...');

        let cpResponse = await this.props.doCompatiblePair(donorId, recipientId, direct, surgeryDate);

        this.props.hideLoading();

        return cpResponse;
    }

    handleRefreshList = () => {
        this.props.getExpertSystemOrders(this.props.centerId)
            .then(response => {
                if (response.data.success) {
                    let expertOrders = response.data.results.map(item => ({ ...item, newComments: item.comments, isChanged: false }));

                    this.setState({
                        expertOrders
                    });
                }
            })
            .finally(() => {
                this.props.hideLoading();
            });
    }

    handlePrintRequisition = (orderId: number, entityTypeId: number) => {
        this.props.showLoading('Downloading requisition file, one moment please...').then(() => {
            this.props.printRequisition(orderId, entityTypeId).then((response: any) => {
                downloadFile(response.data, `requisition-file.pdf`, 'application/pdf');

                this.props.hideLoading();
            });
        });
    }

    handleConfirmWithdrawDonor = (orderId: number) => {
        let data = this.state.expertOrders.find(x => x.expertSystemId == orderId);

        this.setState({
            withdrawDonor: {
                show: true,
                orderId,
                alias: data.donorAlias,
                fullName: data.donorName
            }
        });
    }

    handleWithdrawDonor = () => {
        let orderId = this.state.withdrawDonor.orderId;

        this.setState({
            withdrawDonor: {
                show: false,
                orderId: 0,
                alias: '',
                fullName: ''
            }
        }, () => {
            this.props.showLoading('Clearing expert system order one moment please...').then(() => {
                this.props.updateExpertSystemOrder(orderId, 0)
                    .then(response => {
                        if (response.data.success) {
                            this.handleRefreshList();
                        } else {
                            alert('An error has occurred...');
                        }
                    })
                    .finally(() => {
                        this.props.hideLoading();
                    });
            });
        });
    };

    handleCancelWithdrawDonor = () => {
        this.setState({
            withdrawDonor: {
                show: false,
                orderId: 0,
                alias: '',
                fullName: ''
            }
        });
    }

    handleStatusFilterChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            statusFilter: parseInt(e.currentTarget.value)
        }, () => {
            this.setUrlParameters();
        });
    }

    handleSearchChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            searchFilter: e.currentTarget.value
        }, () => {
            this.setUrlParameters();
        });
    }

    render = () => {
        const list = this.getFilteredSortedList();
        const counts = this.getCounts();

        return (
            <>
                <div className='container-fluid'>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, marginBottom: 20 }}>
                        <NavWrapper width='initial'>
                            <div>
                                <Nav bsStyle='pills' bsSize='sm' className='acc-pills' activeKey={this.props.statusId} onSelect={this.handleTabChange}>
                                    <NavItemWrapper eventKey={Section.PendingDonor}>Pending Donor <Badge>{counts.pendingDonor}</Badge></NavItemWrapper>
                                    <li role='presentation'><span>|</span></li>
                                    <NavItemWrapper eventKey={Section.PendingCenter}>Pending Recipient Selection <Badge>{counts.pendingCenter}</Badge></NavItemWrapper>
                                    <li role='presentation'><span>|</span></li>
                                    <NavItemWrapper eventKey={Section.PendingRecipient}>Pending Recipient <Badge>{counts.pendingRecipient}</Badge></NavItemWrapper>
                                    <li role='presentation'><span>|</span></li>
                                    <NavItemWrapper eventKey={Section.PendingAnalysis}>Pending Analysis <Badge>{counts.pendingAnalysis}</Badge></NavItemWrapper>
                                    <li role='presentation'><span>|</span></li>
                                    <NavItemWrapper eventKey={Section.PendingReview}>Pending Review <Badge>{counts.pendingReview}</Badge></NavItemWrapper>
                                    <li role='presentation'><span>|</span></li>
                                    <NavItemWrapper eventKey={Section.PendingDecision}>Pending Decision <Badge>{counts.pendingDecision}</Badge></NavItemWrapper>
                                    <li role='presentation'><span>|</span></li>
                                    <NavItemWrapper eventKey={Section.Completed}>Completed <Badge>{counts.completed}</Badge></NavItemWrapper>
                                    <li role='presentation'><span>|</span></li>
                                    <NavItemWrapper eventKey={Section.Search}>Search <Badge>{counts.search}</Badge></NavItemWrapper>
                                </Nav>
                            </div>
                        </NavWrapper>
                    </div>

                    <Well bsSize='sm' style={{ marginBottom: 5 }}>
                        <div style={{ display: 'flex' }}>
                            {this.props.statusId == Section.Search &&
                                <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                    <div style={{ paddingRight: 5 }}>Filter by Status:</div>
                                    <div>
                                        <FormControl
                                            componentClass='select'
                                            style={{ width: 300 }}
                                            value={this.state.statusFilter}
                                            onChange={this.handleStatusFilterChange}>
                                            <option value={0}>- ALL -</option>
                                            <option value={1}>Pending Donor</option>
                                            <option value={2}>Pending Center</option>
                                            <option value={3}>Pending Recipient</option>
                                            <option value={4}>Pending Analysis</option>
                                            <option value={5}>Pending Review</option>
                                            <option value={6}>Pending Decision</option>
                                            <option value={7}>Completed</option>
                                        </FormControl>
                                    </div>
                                </div>
                            }

                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ paddingRight: 5 }}>Search by Alias/Name:</div>
                                <div>
                                    <FormControl
                                        placeholder='Please enter at least 3 characters...'
                                        style={{ width: 300 }}
                                        value={this.state.searchFilter}
                                        onChange={this.handleSearchChange} />
                                </div>
                            </div>
                        </div>
                    </Well>

                    <Grid
                        list={list}
                        statusId={this.props.statusId}
                        centerId={this.props.centerId}
                        onShowDonors={this.handleShowDonors}
                        onChangeComments={this.handleUpdateComments}
                        onSaveComments={this.handleSaveComments}
                        onDiscardCommentChanges={this.handleDiscardCommentChanges}
                        onOpenOrder={this.handleOpenOrder}
                        onPrintRequisition={this.handlePrintRequisition}
                        onWithDrawDonor={this.handleConfirmWithdrawDonor}
                    />

                    <div>{list.length}/{this.state.expertOrders.length} orders...</div>
                </div>

                <RecipientDonorsModal
                    show={this.state.recipientDonors.show}
                    data={this.state.recipientDonors.data}
                    orders={this.state.recipientDonors.list}
                    centerId={this.props.centerId}
                    onUpdateExpertSystemOrder={this.handleUpdateExpertSystemOrder}
                    doStandardVoucher={this.handleDoStandardVoucher}
                    doCompatiblePair={this.handleDoCompatiblePair}
                    onRefreshList={this.handleRefreshList}
                    onClose={this.handleDonorCloseModal}
                />

                <WithdrawDonor
                    show={this.state.withdrawDonor.show}
                    alias={this.state.withdrawDonor.alias}
                    fullName={this.state.withdrawDonor.fullName}
                    onConfirmed={this.handleWithdrawDonor}
                    onCancel={this.handleCancelWithdrawDonor} />
            </>
        );
    };
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app
    };
};

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getExpertSystemOrders: (centerId) => {
            return dispatch(ExpertSystemActionCreators.getExpertSystemOrdersByCenter(centerId));
        },
        getExpertSystemOrdersByRecipient: (recipientId) => {
            return dispatch(ExpertSystemActionCreators.getExpertSystemOrdersByRecipient(recipientId));
        },
        updateComments: (orderId, comments) => {
            return dispatch(ExpertSystemActionCreators.updateComments(orderId, comments));
        },
        updateExpertSystemOrder: (orderId, statusId) => {
            return dispatch(ExpertSystemActionCreators.updateExpertSystemOrder(orderId, statusId));
        },
        doStandardVoucher: (donorId, recipientId) => {
            return dispatch(ExpertSystemActionCreators.doStandardVoucher(donorId, recipientId));
        },
        doCompatiblePair: (donorId, recipientId, direct, surgeryDate) => {
            return dispatch(ExpertSystemActionCreators.doCompatiblePair(donorId, recipientId, direct, surgeryDate));
        },
        printRequisition: (orderId, entityType) => {
            return dispatch(ExpertSystemActionCreators.printRequisition(orderId, entityType));
        },
    };
};

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(ExpertSystem);
